/**
 * TransmissionWarnings.js
 * Avertissements de transmission du dashboard — PF-03 CP3.1 (DEC-037).
 *
 * Composant présentationnel alimenté par getTransmissionOverview (catégories
 * en clair, aucun déchiffrement). Une seule ligne par bénéficiaire présent
 * dans overview.byId (les terminaux n'y figurent pas), par priorité :
 *   1. transmission non préparée (ambre) ;
 *   2. enveloppe vide : aucun item dans les catégories attribuées (ambre) ;
 *   3. code self_custody : perte du code = transmission impossible (neutre),
 *      avec un lien « Prévenir un proche » si aucun contact de confiance (CP3.2).
 */

import React from 'react';

const AMBER = '#F59E0B';
const G = '#8892A4';

const firstNameOf = (beneficiary) => (beneficiary.name || '').trim().split(/\s+/)[0] || 'votre bénéficiaire';

function warningFor(beneficiary, entry) {
  const name = firstNameOf(beneficiary);
  if (!entry.prepared) {
    return { tone: 'amber', text: `Transmission non préparée pour ${name}.`, actionLabel: 'Préparer' };
  }
  if (entry.itemCount === 0) {
    return {
      tone: 'amber',
      text: `${name} ne recevra rien : aucun élément du coffre ne correspond aux catégories que vous lui avez attribuées.`,
      actionLabel: 'Modifier',
    };
  }
  if (entry.releasePath === 'self_custody') {
    return {
      tone: 'neutral',
      text: `Si le code de transmission de ${name} est perdu, la transmission ne sera pas possible.`,
      actionLabel: entry.hasTrustedContact === false ? 'Prévenir un proche' : undefined,
    };
  }
  return null;
}

const TransmissionWarnings = ({ beneficiaries, overview, onNavigate }) => {
  if (!overview?.byId) return null;

  const lines = (beneficiaries || [])
    .filter((b) => overview.byId[b.id])
    .map((b) => ({ id: b.id, ...warningFor(b, overview.byId[b.id]) }))
    .filter((line) => line.text);

  if (lines.length === 0) return null;

  return (
    <div aria-label="Avertissements de transmission" style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
      {lines.map((line) => {
        const amber = line.tone === 'amber';
        return (
          <div
            key={line.id}
            data-tone={line.tone}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
              padding: '10px 14px',
              borderRadius: 8,
              fontSize: 13,
              lineHeight: 1.5,
              background: amber ? 'rgba(245,158,11,0.08)' : 'rgba(255,255,255,0.03)',
              border: amber ? '1px solid rgba(245,158,11,0.25)' : '1px solid rgba(255,255,255,0.1)',
              color: amber ? AMBER : G,
            }}
          >
            <span>{line.text}</span>
            {line.actionLabel && (
              <button
                type="button"
                onClick={() => onNavigate('/beneficiaries')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: amber ? AMBER : '#e2e8f0',
                  fontSize: 13,
                  fontWeight: 600,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                {line.actionLabel}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TransmissionWarnings;
